import { css } from '@emotion/core';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { Button } from 'react-bootstrap';

import { AngularServicesContext } from 'react-app';
import { RootState } from 'redux/schemas';
import { VideoPracticeFeedbackActivity, VideoPracticeFeedbackLectureComponentType } from 'redux/schemas/models/video-practice-feedback';
import { VideoPracticeSubmission } from 'redux/schemas/models/video-practice';
import { ActivityType } from 'redux/schemas/models/activity';
import { getCurrentCourse } from 'redux/selectors/course';
import { getFeedbackGallerySubmissions } from 'redux/selectors/video-practice-feedback';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import {
  getAvailablePracticeActivities,
  setFeedbackActivity,
  setRequirementSubmitted,
  setShowFeedbackInstruction,
  setShowPracticeFeedbackModal,
} from 'redux/actions/video-practice-feedback';

import ActivityStatus from 'shared/components/activity/activity-status';
import ActivityTitle from 'shared/components/activity/activity-title';
import ActivityPoints from 'shared/components/activity/activity-points';
import ForActivityTitle from 'shared/components/activity/for-activity-title';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import NvIcon from 'shared/components/nv-icon';
import LectureComponentDeadline from 'components/lecture-component-deadline';
import LectureComponentProvider, { useLectureComponentContext } from 'components/lecture-component-provider';
import { FroalaViewMode } from 'froala/helpers/nv-froala-constants';
import NvFroala from 'froala/components/nv-froala';
import {
  BaseLectureComponentContext,
} from 'lecture_pages/directives/components/base-lecture-component';
import NvSubmissionGallery from 'shared/components/nv-submission-gallery';
import Portal from 'shared/components/portal';
import PracticeFeedbackOverview from 'lecture_pages/components/video-practice-evaluation/feedback-overview/practice-feedback-overview';
import { LectureComponentProps, LecturePageMode } from 'lecture_pages/components';
import VideoPracticeSpecialGalleryCard from 'lecture_pages/components/video-practice/video-practice-special-gallery-card';
import VideoPracticeGalleryCard from 'lecture_pages/components/video-practice/video-practice-gallery-card';

import { danger, hexToRgbaString } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';
import { notHandheld } from 'styles/global_defaults/media-queries';
import { ComponentType, NLectureComponent } from 'redux/schemas/models/lecture-component';
import { config } from '../../../../config/pendo.config.json';

const styles = css`
  .activity-container {
    max-width: 800px;
  }
  .unavailable {
    background-color: ${hexToRgbaString(danger, 0.05)};
    border: 1px dashed ${danger};
  }
  .for-activity {
    white-space: nowrap;
  }
  &.video-practice-gallery {
    margin-left: -${standardSpacing}px;
    margin-right: -${standardSpacing}px;

    .nv-submission-gallery {
      padding-top: ${standardSpacing}px;

      .carousel-next, .carousel-prev {
        margin-top: ${standardSpacing}px;
        ${notHandheld(css`
          height: 240px;
        `)}
      }
    }
  }
`;

const useGetVideoPracticeFeedbackLectureComponent = (passedLectureComponentId?: number) => {
  const contextLectureComponentId = useLectureComponentContext().lectureComponentId;

  const lectureComponentId = String(passedLectureComponentId ?? contextLectureComponentId);
  return useSelector<RootState, VideoPracticeFeedbackLectureComponentType>((state: RootState) => state.models.lectureComponents[lectureComponentId] as NLectureComponent<ComponentType.VIDEO_PRACTICE_FEEDBACK>);
};

const useUpdatePracticeFeedbackActivity = () => {
  const dispatch = useAppDispatch();

  const { catalogId, lecturePageId } = useLectureComponentContext();
  const { id, type, index, practiceFeedbackActivity: practiceFeedbackActivityId } = useGetVideoPracticeFeedbackLectureComponent();
  const activity = useSelector<RootState, VideoPracticeFeedbackActivity>((state) => state.models.practiceFeedbackActivities[practiceFeedbackActivityId]);

  return useCallback(
    (newPracticeFeedbacActivity: Partial<VideoPracticeFeedbackActivity>) => {
      const updatedComponent = {
        catalogId,
        lecturePageId,
        componentData: {
          id,
          type,
          index,
          publicFeedback: {
            ...activity,
            activityType: ActivityType.VIDEO_PRACTICE,
            ...newPracticeFeedbacActivity,
          },
        },
      };

      return dispatch(updateLectureComponent(updatedComponent)).then(() => {
        dispatch(getAvailablePracticeActivities({ catalogId }));
      });
    },
    [dispatch, catalogId, index, id, lecturePageId, type, activity],
  );
};

const NotAvailableGreyContainer = (props: { icon: string, text: string }) => (
  <div className='bg-gray-6 d-flex flex-column justify-content-between align-items-center p-4 text-gray-3 w-100 my-5'>
    <NvIcon icon={props.icon} size='medium-large' />
    <div className='mt-2'>{props.text}</div>
  </div>
);

const VideoPracticeEvaluationComponentContextWrapper = (props: LectureComponentProps<ComponentType.VIDEO_PRACTICE_FEEDBACK>) => {
  const isEdit = props.mode === LecturePageMode.EDIT;

  const catalogId = useSelector((state) => state.app.currentCatalogId);

  return (
    <LectureComponentProvider
      catalogId={catalogId}
      lectureComponentId={props.lectureComponent.id}
      lecturePageId={props.currentLecture.id}
      isEdit={isEdit}
      angularComponent={props.angularComponent}
    >
      <VideoPracticeEvaluationComponent {...props} />
    </LectureComponentProvider>
  );
};

const VideoPracticeEvaluationComponent = ({
  currentLecture,
}: LectureComponentProps<ComponentType.VIDEO_PRACTICE_FEEDBACK>) => {
  const { $state, $uibModal } = useContext(AngularServicesContext);
  const { sharedProps, setSharedProps, extraContentAreaRef } = useContext(BaseLectureComponentContext);

  const dispatch = useAppDispatch();

  const { isEdit, catalogId, lectureComponentId } = useLectureComponentContext();
  const activityUpdater = useUpdatePracticeFeedbackActivity();

  const lectureComponent = useSelector(state => state.models.lectureComponents[lectureComponentId]);
  const activity = useSelector<RootState, VideoPracticeFeedbackActivity>(state => state.models.practiceFeedbackActivities[lectureComponent.practiceFeedbackActivity]);
  const currentCourse = useSelector((state) => getCurrentCourse(state));
  const feedbackGallery: VideoPracticeSubmission[] = useSelector(state => getFeedbackGallerySubmissions(state, activity.id));
  const showModal: boolean = useSelector(state => state.app.videoPracticeFeedback?.showPracticeFeedbackModal);
  const userHasViewdInstruction: boolean = useSelector(state => state.app.videoPracticeFeedback?.userHasViewdInstruction?.[activity.id]);

  const [activityInfo, setActivityInfo] = useState(activity?.description);
  const [modalState, setModalState] = useState<{ submissionId: number, scenarioId: number }>(null);
  const [activityTitle, setactivityTitle] = useState(activity.title);

  const pendingEvaluationCount = activity.requiredToComplete - activity.numReviewesCompleted;
  const practiceActivity = activity?.activity;
  const isPracticeActivityUnreleased = moment(practiceActivity?.releaseDate).isAfter(moment());

  useEffect(() => {
    const extraOptions = [];

    extraOptions.push({ type: 'text', text: t.LECTURE_PAGES.COMPONENTS.DROPDOWN.EDIT_BASICS(), callback: () => openEditModal() });
    if (!currentCourse.isSelfPaced) {
      extraOptions.push({
        type: 'text',
        text: activity.deadline ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.REMOVE_DEADLINE() : t.LECTURE_PAGES.COMPONENTS.DROPDOWN.ADD_DEADLINE(),
        callback: () => {
          activityUpdater({
            deadline: activity.deadline
              ? null
              : moment(currentLecture.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
          });
        },
      });
    }
    extraOptions.push({
      type: 'text',
      text: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.VIEW_PRACTICE(),
      callback: () => gotoVideoPractice(true),
    });

    setSharedProps({
      ...sharedProps,
      deleteConfirmationTitle: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.DELETE_CONFIRMATION.TITLE(),
      deleteConfirmationMessage: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.DELETE_CONFIRMATION.DESCRIPTION(),
      extraOptions: {
        mode: 'prepend',
        options: extraOptions,
      },
    });
  }, [activity]);

  useEffect(() => {
    if (modalState !== null) {
      dispatch(setShowPracticeFeedbackModal(true));
    }
  }, [dispatch, modalState]);

  useEffect(() => {
    if (practiceActivity?.progress === 'completed' && !activity?.hasSubmittedRequirement) {
      dispatch(setRequirementSubmitted(activity?.id));
    }
  }, [activity?.hasSubmittedRequirement, activity?.id, dispatch, practiceActivity?.progress]);

  const onSelectSubmission = useCallback((submissionId: number, scenarioId: number) => {
    setModalState({
      submissionId,
      scenarioId,
    });
    dispatch(setFeedbackActivity(activity.id));
    if (!userHasViewdInstruction && activity?.progress === 'not_started') {
      dispatch(setShowFeedbackInstruction({
        show: true,
        feedbackCriteriaId: activity.id,
      }));
    } else {
      dispatch(setShowFeedbackInstruction({
        show: false,
        feedbackCriteriaId: activity.id,
      }));
    }
  }, [activity.id, activity?.progress, dispatch, userHasViewdInstruction]);

  const getFeedbackGalleryCards = useCallback(() => {
    if (feedbackGallery?.length > 0 && !isEdit) {
      const gallerycards = feedbackGallery.map(submission => (
        <VideoPracticeGalleryCard
          key={submission.id}
          {...submission}
          onClick={() => onSelectSubmission(submission.id, submission.scenario.id)}
          featured={false} // Not to display highlight
          isFeedbackGallery
          dataQa={`practice-feedback-view-practice-${submission.id}`}
          dataQaPendo={config.pendo.practiceFeedback.viewPractice}
        />
      ));
      if (feedbackGallery.length < activity?.requiredToComplete) {
        gallerycards.push(
          <VideoPracticeSpecialGalleryCard
            key='no-submissions'
            title={t.LECTURE_PAGES.COMPONENTS.VIDEO_PRACTICE.GALLERY.NO_SUBMISSIONS()}
          />,
        );
      }
      return gallerycards;
    }
    return null;
  }, [activity?.requiredToComplete, feedbackGallery, isEdit, onSelectSubmission]);

  const openEditModal = () => {
    const modalInstance = $uibModal.open({
      templateUrl: 'lecture_pages/templates/components/video-practice-evaluation-modal.html',
      controller: 'VideoPracticeEvaluationCtrl',
      controllerAs: 'vm',
      resolve: {
        vmResolves: {
          formData: {
            activity: practiceActivity,
            activityId: activity?.activityId,
            activityType: ActivityType.VIDEO_PRACTICE,
            requiredToComplete: activity.requiredToComplete,
            title: activityTitle,
            description: activityInfo,
            hardDeadline: activity.hardDeadline,
            isTodo: activity.isTodo,
          },
          isEdit: true,
          lectureComponent,
        },
      },
    });

    modalInstance.result.then((res) => {
      activityUpdater(res);
    });
  };

  const gotoVideoPractice = (isEditMode?: boolean) => {
    const stateParams = {
      page: 'lecture-page',
      catalogId,
      id: practiceActivity?.lecturePageId,
      lectureActivityId: practiceActivity?.lectureComponentId,
    };
    if (isEditMode) {
      stateParams.page = 'lecture-page-edit';
    }
    const { page, ...restParams } = stateParams;
    /**
     * doing an angular-ui-router transition via $state.go() doesn't seem to update the template,
     * so we're using the same alternative through 'document.location.hash'
     */
    document.location.hash = $state.href(page, restParams);
  };

  const galleryContent = (
    <div css={styles} className='video-practice-gallery'>
      <NvSubmissionGallery
        spacing={30}
        arrowsHidden={!feedbackGallery.length}
        bottomPadding={40}
      >
        {getFeedbackGalleryCards()}
      </NvSubmissionGallery>
    </div>
  );

  const hasNotAvailableContent = ((isPracticeActivityUnreleased && isEdit) || (activity.hasSubmittedRequirement && !feedbackGallery.length && !isEdit));
  const notAvailableContent = useCallback(() => {
    if (isPracticeActivityUnreleased && isEdit && practiceActivity?.releaseDate) {
      return (
        <NotAvailableGreyContainer
          icon='zoom'
          text={t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.BEFORE_ASSIGNMENT_RELEASE_DATE(
            moment(practiceActivity?.releaseDate).format('LLL'),
          )}
        />
      );
    }
    if (activity.hasSubmittedRequirement && !feedbackGallery.length && !isEdit) {
      return (
        <NotAvailableGreyContainer
          icon='zoom'
          text={t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.EMPTY()}
        />
      );
    }
    return null;
  }, [
    activity.hasSubmittedRequirement, feedbackGallery.length, isEdit,
    isPracticeActivityUnreleased, practiceActivity?.releaseDate,
  ]);

  const onClosePracticeModal = useCallback(() => {
    dispatch(setShowPracticeFeedbackModal(false));
    setModalState(null);
    dispatch(setFeedbackActivity(null));
  }, [dispatch]);

  useEffect(() => {
    // To track the outside click event of the modal and close it.
    const closeAll = (e) => {
      if (e.target.classList.contains('bs4-modal-content')) {
        onClosePracticeModal();
      }
    };

    if (showModal) {
      document.addEventListener('click', closeAll);
    }

    return () => {
      if (showModal) {
        document.removeEventListener('click', closeAll);
      }
    };
  }, [onClosePracticeModal, showModal]);

  return (
    <div
      className='d-flex flex-column'
      id={`lecture-component-${lectureComponentId}`}
      css={styles}
    >
      {activity && (
        <div className='d-flex flex-column activity-container mx-auto justify-content-center align-items-center w-100'>
          <ActivityStatus icon='peer-feedback' status={activity.progress ?? 'not_started'} />
          <div className='text-small d-flex flex-column align-items-center mt-2'>
            <ActivityTitle
              status={activity.progress ?? 'not_started'}
              statusesTexts={{
                not_started: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.PROVIDE_FEEDBACK_DESCRIPTION.NOT_STARTED(activity.requiredToComplete ?? 0),
                completed: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.PROVIDE_FEEDBACK_DESCRIPTION.COMPLETED(activity.numReviewesCompleted ?? 0),
                missed: t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.PROVIDE_FEEDBACK_DESCRIPTION.MISSED(pendingEvaluationCount ?? 0, activity.requiredToComplete ?? 0),
              }}
            />
            {activity.progress === 'in_progress' && (
              <span className='font-weight-bold mt-2'>
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.PROVIDE_FEEDBACK_DESCRIPTION.IN_PROGRESS(pendingEvaluationCount, activity.numReviewesCompleted)}
              </span>
            )}
          </div>
          <LectureComponentHeaderInput
            value={activityTitle}
            editable={isEdit}
            onBlur={() => {
              activityUpdater({ title: activityTitle });
            }}
            onChange={(newTitle) => setactivityTitle(newTitle)}
            required
          />
          {practiceActivity && (
            <ForActivityTitle
              title={t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.FOR_ACTIVITY()}
              activity={practiceActivity.title}
              titleclassName='text-small font-weight-bolder for-activity'
              activityClassName='d-block text-small font-weight-bold text-primary ml-1 ellipsis'
              gotoActivty={() => gotoVideoPractice(isEdit)}
              dataQa={config.pendo.practiceFeedback.forActivity}
            />
          )}
          <div className='my-2'>
            <LectureComponentDeadline
              componentName='video-practice-feedback-deadline'
              expirationDate={activity.deadline}
              lecturePageId={currentLecture.id}
              catalogId={catalogId}
              isEdit={isEdit}
              hasHardDeadlines={activity.hardDeadline}
              onHardDeadlineChange={(hardDeadline) => {
                activityUpdater({ hardDeadline });
              }}
              onDeadlineChange={(date) => {
                activityUpdater({ deadline: date?.toISOString() });
              }}
            />
          </div>
          {activity.pointsConfiguration && (
            <div className='mb-2'>
              {activity.progress === 'in_progress' ? (
                <div className='d-flex text-small font-weight-bold'>
                  <NvIcon icon='highlight' size='xss-smallest' className='text-success mr-2' />
                  <span>
                    <span className='text-success'>{activity.pointsReceived}</span>
                    <span className='text-gray-2'>
                      /
                      {activity.totalPoints?.[0]}
                    </span>
                  </span>
                </div>
              ) : (
                <ActivityPoints
                  points={
                    activity.progress === 'not_started' || isEdit
                      ? activity.pointsConfiguration?.points
                      : activity.pointsReceived
                  }
                  isCompleted={activity.progress === 'completed'}
                  showPointsEach={activity.progress === 'not_started' || isEdit}
                  totalPoints={activity.totalPoints?.[0]}
                  progress={activity.progress}
                />
              )}

            </div>
          )}
          <div className='w-100'>
            {isEdit && (
              <NvFroala
                value={activityInfo}
                onChange={setActivityInfo}
                preset={FroalaViewMode.INLINE}
                immediateReactModelUpdate
                onBlur={() => {
                  activityUpdater({ description: activityInfo });
                }}
              />
            )}
            {(!isEdit && (activity.hasSubmittedRequirement || !practiceActivity)) && (
              <div
                className='text-body'
                dangerouslySetInnerHTML={{ __html: activity.description }}
              />
            )}
          </div>
          {/* practice activity removed - admin view */}
          {!practiceActivity && isEdit && (
            <div className='unavailable d-flex flex-column align-items-center justify-content-center p-2 my-5 w-100'>
              <div className='font-weight-bolder gray-1'>
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.PICK_ACTIVITY()}
              </div>
              <div className='gray-1 my-1'>
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.DESCRIPTION()}
              </div>
              <Button variant='primary mt-2' onClick={openEditModal}>
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.PICK()}
              </Button>
            </div>
          )}
          {((!practiceActivity || isPracticeActivityUnreleased) && !isEdit) && (
            <NotAvailableGreyContainer icon='ban' text={t.LECTURE_PAGES.COMPONENTS.NOT_AVAILABLE_YET()} />
          )}
          {!activity.hasSubmittedRequirement && !isEdit && practiceActivity && !isPracticeActivityUnreleased && (
            <div className='d-flex flex-column align-items-center'>
              {activity.progress !== 'missed' && (
                <div className='text-small gray-3 text-center'>
                  {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.NOT_AVAILABLE.LEANER_NOT_COMPLETED_ASSIGNMENT(practiceActivity.title)}
                </div>
              )}
              <Button
                variant='primary'
                onClick={() => gotoVideoPractice()}
                className='my-2'
                data-qa={config.pendo.practiceFeedback.gotoPractice}
              >
                {t.LECTURE_PAGES.COMPONENTS.PEER_EVALUATION.VIDEO_PRACTICE.GO_TO_PRACTICE()}
              </Button>
            </div>
          )}
        </div>
      )}
      {((feedbackGallery.length > 0 && !isEdit) || hasNotAvailableContent) && (
        <Portal ref={extraContentAreaRef}>
          {notAvailableContent()}
          {!isPracticeActivityUnreleased && galleryContent}
        </Portal>
      )}
      {modalState && (
        <NvModal
          backdrop
          type={ModalType.NO_DIALOG}
          show={showModal}
          onClose={onClosePracticeModal}
          closeDataQa={config.pendo.practiceFeedback.closeOverviewModal}
          body={(
            <PracticeFeedbackOverview
              submissionId={modalState.submissionId}
              scenarioId={modalState.scenarioId}
            />
          )}
        />
      )}
    </div>
  );
};

export default VideoPracticeEvaluationComponentContextWrapper;
